import React, { useState, useContext } from 'react';
import { Box, Paper, TextField, Button, Typography, Container, Alert, InputAdornment, IconButton, useTheme } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import { ColorModeContext } from '../App';

const Login = ({ onLogin }) => {
  const [creds, setCreds] = useState({ user: '', pass: '' });
  const [error, setError] = useState('');
  const theme = useTheme();
  const colorMode = useContext(ColorModeContext);

  const commonInputSx = {
    '& .MuiInputBase-root': {
      borderRadius: 3,
      bgcolor: theme.palette.mode === 'dark' ? '#1D2230' : '#F0F2F5',
      overflow: 'hidden',
      transition: 'background-color 0.2s, box-shadow 0.2s, border-color 0.2s',
      border: '1px solid',
      borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'transparent',
      '&:hover': {
        bgcolor: theme.palette.mode === 'dark' ? '#232836' : '#E6E9EC',
      },
      '&.Mui-focused': {
        bgcolor: theme.palette.mode === 'dark' ? '#232836' : '#fff',
        borderColor: 'primary.main',
        boxShadow: theme.palette.mode === 'dark' ? '0 0 0 4px rgba(0,242,255,0.1)' : '0 0 0 4px rgba(0,120,255,0.1)',
      }
    },
    '& .MuiInputBase-input': {
      py: 1.5,
      fontSize: '1rem',
      fontWeight: 500,
      '&:-webkit-autofill': {
        WebkitBoxShadow: theme.palette.mode === 'dark' ? '0 0 0 100px #1D2230 inset' : '0 0 0 100px #F0F2F5 inset',
        WebkitTextFillColor: theme.palette.mode === 'dark' ? '#fff' : 'inherit',
        caretColor: theme.palette.mode === 'dark' ? '#fff' : 'inherit',
        borderRadius: 'unset',
      },
      '&:-webkit-autofill:hover, &:-webkit-autofill:focus': {
        WebkitBoxShadow: theme.palette.mode === 'dark' ? '0 0 0 100px #1D2230 inset' : '0 0 0 100px #F0F2F5 inset',
      }
    },
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (creds.user === 'admin' && creds.pass === 'efficiencyapps') {
      onLogin();
    } else {
      setError('Invalid credentials');
    }
  };

  return (
    <Container maxWidth={false} sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative'
    }}>
      {/* Theme Toggle Button */}
      <Box sx={{ position: 'absolute', top: 20, right: 20, zIndex: 10 }}>
        <IconButton sx={{ ml: 1 }} onClick={colorMode.toggleColorMode} color="inherit">
          {theme.palette.mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
        </IconButton>
      </Box>

      <Paper elevation={24} sx={{
        p: { xs: 3, md: 6 },
        width: '100%',
        maxWidth: 450,
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 4,
        border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.05)',
      }}>
        {/* Decorative ambient glow */}
        <Box sx={{
          position: 'absolute',
          top: '-50%',
          left: '-50%',
          width: '200%',
          height: '200%',
          background: theme.palette.mode === 'dark'
            ? 'radial-gradient(circle, rgba(0,242,255,0.03) 0%, rgba(0,0,0,0) 60%)'
            : 'radial-gradient(circle, rgba(0,120,255,0.08) 0%, rgba(0,0,0,0) 60%)',
          zIndex: 0,
          pointerEvents: 'none'
        }} />

        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h3" sx={{
            fontWeight: 800,
            mb: 1,
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(45deg, #fff 30%, #94a3b8 90%)'
              : 'linear-gradient(45deg, #1e293b 30%, #475569 90%)',
            backgroundClip: 'text',
            textFillColor: 'transparent',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Efficiency Apps
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 5, letterSpacing: 0.5 }}>
            Secure Utility Suite
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

          <form onSubmit={handleLogin}>
            <TextField
              fullWidth
              placeholder="Username"
              sx={{
                mb: 2.5,
                ...commonInputSx
              }}
              value={creds.user}
              onChange={(e) => setCreds({ ...creds, user: e.target.value })}
              InputProps={{
                disableUnderline: true,
                startAdornment: <InputAdornment position="start" sx={{ mr: 1 }}><PersonIcon color="action" /></InputAdornment>
              }}
            />
            <TextField
              fullWidth
              type="password"
              placeholder="Password"
              sx={{
                mb: 4,
                ...commonInputSx
              }}
              value={creds.pass}
              onChange={(e) => setCreds({ ...creds, pass: e.target.value })}
              InputProps={{
                disableUnderline: true,
                startAdornment: <InputAdornment position="start" sx={{ mr: 1 }}><LockIcon color="action" /></InputAdornment>
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              sx={{
                py: 1.8,
                fontSize: '1rem',
                fontWeight: 700,
                letterSpacing: 1
              }}
            >
              AUTHENTICATE
            </Button>
          </form>
        </Box>
      </Paper>
    </Container>
  );
};

export default Login;
